function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import T from 'prop-types';
import React, { useMemo } from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { reach } from 'yup';
import useBreakpoint from '../../hooks/useBreakpoint';
import useMembershipButton from '../../hooks/useMembershipButton';
import { copyJob, hideModal, listProjects, showModal, updateProjectName } from '../../redux/actions';
import { profileSelectors } from '../../redux/selectors/profile';
import { isFailed, isSuccess } from '../../redux/stateTools';
import routes from '../../routes/constants';
import text from '../../text';
import { formatDate } from '../../utilities/date';
import { isDemoJob, jobHasPointCloud } from '../../utilities/job';
import { PROJ_NAME_MAX_LENGTH, projectValidationSchema } from '../../validation';
import LinkButton from '../button/LinkButton';
import MeatballDropdown from '../dropdown/MeatballDropdown';
import Icon from '../icon/Icon';
import { DELETE_PROJECT_MODAL } from '../modal/DeleteProjectModal';
import { MERGE_PROJECT_MODAL } from '../modal/MergeProjectModal';
import { INVITE_PROJECT_USERS_MODAL } from '../modal/inviteProjectUsersModal/InviteProjectUsersModal';
import Cell from '../table/Cell';
import Status from './Status';
import { NON_EDITABLE_STATUSES } from './consts';
export var ORDER_LISTING_HEIGHT_PX = 76; // KEEP THIS in coordination with $order-listing-height scss variable
export var COLUMN = {
    DATE: 'date-column',
    NAME: 'name-column',
    ADDRESS: 'address-column',
    STATUS: 'status-column',
    FILE_MANAGER: 'file-manager-column',
    VIEW3D: 'view3D-column',
    ACTIONS: 'actions-column',
    ORDER_COUNT: 'order-count-column'
};
var BadgeCount = function(param) {
    var count = param.count;
    if (count <= 0) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Badge, {
        className: "badge-greyed badge--padded-top"
    }, "+ ".concat(count));
};
BadgeCount.propTypes = {
    count: T.number
};
var OrderListingItem = function(param) {
    var job = param.job, isAdminOrigin = param.isAdminOrigin, isStaff = param.isStaff, _param_style = param.style, style = _param_style === void 0 ? {} : _param_style;
    var _job_user_project, _job_user_project1, _job_project, _job_ui, _job_ui1, _job_project1, _job_project2;
    var dispatch = useDispatch();
    var history = useHistory();
    var isUpgradeRecommended = useSelector(profileSelectors.getIsUpgradeRecommended);
    var canDeleteAnyProject = useSelector(profileSelectors.getCanDeleteAnyProject);
    var isDeleteEnabled = useMemo(function() {
        var _job_jobs;
        return job.status !== 'demo' && ((job === null || job === void 0 ? void 0 : (_job_jobs = job.jobs) === null || _job_jobs === void 0 ? void 0 : _job_jobs.every(function(item) {
            return item.status == 'draft';
        })) || canDeleteAnyProject);
    }, [
        job,
        canDeleteAnyProject
    ]);
    var is3dViewerAccessibleToUser = useSelector(profileSelectors.get3dViewerEnabled);
    var isAdmin = useSelector(profileSelectors.getIsAdmin);
    var handleMembershipClick = useMembershipButton().handleMembershipClick;
    var is3dViewerLocked = jobHasPointCloud(job) && !is3dViewerAccessibleToUser && !isDemoJob(job);
    var isEditingEnabledForBkp = useBreakpoint([
        'sm',
        'md',
        'lg',
        'xl'
    ]);
    var isPending = ((_job_user_project = job.user_project) === null || _job_user_project === void 0 ? void 0 : _job_user_project.invite_status) === 'pending';
    var _3dViewerRoute = isAdminOrigin ? "".concat(routes.view3DExternal.project(job.projectId), "?admin=true") : "".concat(routes.view3DExternal.project(job.projectId));
    var handleDeleteProject = function() {
        return dispatch(showModal(DELETE_PROJECT_MODAL, {
            projectId: job === null || job === void 0 ? void 0 : job.projectId,
            name: job === null || job === void 0 ? void 0 : job.project.name
        }));
    };
    var handleAddOrderToProject = function() {
        var _job_project;
        return history.push({
            pathname: routes.order.root,
            search: "?projectId=".concat(job === null || job === void 0 ? void 0 : (_job_project = job.project) === null || _job_project === void 0 ? void 0 : _job_project.id)
        });
    };
    var handleMergeProject = function() {
        return dispatch(showModal(MERGE_PROJECT_MODAL, {
            currentJob: job,
            onSuccess: function() {
                return dispatch(listProjects(isAdmin));
            }
        }));
    };
    var onUpdateProjectName = function(projectName) {
        return dispatch(updateProjectName(job.projectId, projectName.trim(), isAdmin));
    };
    var onEdit = function() {
        history.push("/order/".concat(job.id));
    };
    var onCopy = function() {
        dispatch(copyJob(job.id, isAdmin));
    };
    var jobCountExceptCurrent = useMemo(function() {
        if (!(job === null || job === void 0 ? void 0 : job.jobs)) return 0;
        return job.jobs.length - 1;
    }, [
        job
    ]);
    var _2dMapRoute = (job === null || job === void 0 ? void 0 : job.id) ? job.mp_enabled ? routes.mp.job(job.id) : routes.order.job(job.id) : routes.mp.root;
    var view3dTooltipText = isUpgradeRecommended ? text('upgradeToView3dTooltip') : text('startTrialToView3dTooltip');
    return !isEmpty(job) && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("tr", {
        style: style,
        className: classNames({
            'has-pending-invite': ((_job_user_project1 = job.user_project) === null || _job_user_project1 === void 0 ? void 0 : _job_user_project1.invite_status) === 'pending',
            'has-non-staff': job.has_customer && isStaff,
            'is-other': (_job_project = job.project) === null || _job_project === void 0 ? void 0 : _job_project.is_other
        })
    }, /*#__PURE__*/ React.createElement(Cell, {
        className: COLUMN.NAME,
        disabled: NON_EDITABLE_STATUSES.includes(job.status) || !isEditingEnabledForBkp,
        isFailed: isFailed((_job_ui = job.ui) === null || _job_ui === void 0 ? void 0 : _job_ui.updateState),
        isSuccessful: isSuccess((_job_ui1 = job.ui) === null || _job_ui1 === void 0 ? void 0 : _job_ui1.updateState),
        id: job.id,
        maxLength: PROJ_NAME_MAX_LENGTH,
        onSubmit: onUpdateProjectName,
        validationSchema: reach(projectValidationSchema, 'project.name'),
        value: isNull((_job_project1 = job.project) === null || _job_project1 === void 0 ? void 0 : _job_project1.name) ? text('untitled') : (_job_project2 = job.project) === null || _job_project2 === void 0 ? void 0 : _job_project2.name
    }, /*#__PURE__*/ React.createElement(Status, {
        className: "status-in-name",
        job: job
    })), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.ADDRESS
    }, /*#__PURE__*/ React.createElement("div", {
        className: "d-flex flex-column flex-xl-row align-items-xl-end"
    }, /*#__PURE__*/ React.createElement("div", null, job.full_address))), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.ORDER_COUNT
    }, /*#__PURE__*/ React.createElement(BadgeCount, {
        count: jobCountExceptCurrent
    })), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.DATE
    }, /*#__PURE__*/ React.createElement("p", {
        className: "mb-0"
    }, formatDate(job.created_at)), /*#__PURE__*/ React.createElement("p", {
        className: "mb-0"
    }, job.creator && job.creator.full_name ? job.creator.full_name : null)), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.DATE
    }, /*#__PURE__*/ React.createElement("p", {
        className: "mb-0"
    }, formatDate(job.updated_at)), /*#__PURE__*/ React.createElement("p", {
        className: "mb-0"
    }, job.updater && job.updater.full_name ? job.updater.full_name : null)), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.STATUS
    }, /*#__PURE__*/ React.createElement(Status, {
        job: job
    })), /*#__PURE__*/ React.createElement("td", {
        className: COLUMN.ACTIONS
    }, /*#__PURE__*/ React.createElement("div", {
        className: "job-actions"
    }, /*#__PURE__*/ React.createElement(LinkButton, _object_spread({
        className: classNames('table-icon-button', {
            'star-container': is3dViewerLocked
        }),
        tooltipProps: {
            text: text(is3dViewerLocked ? view3dTooltipText : jobHasPointCloud(job) ? 'viewer3D' : 'viewer3dUnavailable')
        },
        disabled: isPending || !is3dViewerLocked && !isDemoJob(job) && (!jobHasPointCloud(job) || !is3dViewerAccessibleToUser)
    }, !isPending ? is3dViewerLocked ? {
        onClick: function(e) {
            e.preventDefault();
            handleMembershipClick({
                redirectTo: {
                    path: _3dViewerRoute,
                    label: text('navigateToRoute', {
                        navigation: 'Continue',
                        routeName: text('viewer3D')
                    })
                }
            });
        }
    } : jobHasPointCloud(job) ? {
        to: _3dViewerRoute
    } : {} : {}), /*#__PURE__*/ React.createElement(Icon, {
        icon: "3D-with-text"
    }), is3dViewerLocked ? /*#__PURE__*/ React.createElement("div", {
        className: "lock-badge-container"
    }, /*#__PURE__*/ React.createElement(Icon, {
        icon: "lock"
    })) : null), /*#__PURE__*/ React.createElement(LinkButton, {
        disabled: isPending,
        tooltipProps: {
            text: text('map2D')
        },
        to: job.lga ? routes.order.root : isAdminOrigin ? "".concat(_2dMapRoute, "?admin=true") : _2dMapRoute,
        className: "table-icon-button"
    }, /*#__PURE__*/ React.createElement(Icon, {
        icon: "2D-with-text"
    })), /*#__PURE__*/ React.createElement(LinkButton, {
        disabled: isPending,
        tooltipProps: {
            text: text('fileManager')
        },
        to: isAdminOrigin ? "".concat(routes.fileManager.viewProject(job.project_id), "?admin=true") : "".concat(routes.fileManager.viewProject(job.project_id)),
        className: "table-icon-button"
    }, /*#__PURE__*/ React.createElement(Icon, {
        icon: "file-with-text"
    })), /*#__PURE__*/ React.createElement(MeatballDropdown, {
        btnClassName: "table-icon-button",
        disabled: isPending
    }, /*#__PURE__*/ React.createElement(Dropdown.Item, {
        disabled: [
            'demo'
        ].includes(job.status),
        onClick: function() {
            return dispatch(showModal(INVITE_PROJECT_USERS_MODAL, {
                onHide: function() {
                    return dispatch(hideModal(INVITE_PROJECT_USERS_MODAL));
                },
                project: job === null || job === void 0 ? void 0 : job.project,
                job: job
            }));
        }
    }, text('inviteViewProjectUsers')), /*#__PURE__*/ React.createElement(Dropdown.Item, {
        disabled: ![
            'draft'
        ].includes(job.status),
        onClick: onEdit
    }, "Edit"), job.status !== 'demo' && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Dropdown.Item, {
        onClick: onCopy
    }, "Duplicate"), /*#__PURE__*/ React.createElement(Dropdown.Item, {
        onClick: handleAddOrderToProject
    }, text('addNewOrder')), /*#__PURE__*/ React.createElement(Dropdown.Item, {
        onClick: handleMergeProject
    }, text('mergeProjects'))), /*#__PURE__*/ React.createElement(Dropdown.Item, {
        disabled: !isDeleteEnabled,
        onClick: handleDeleteProject
    }, text('deleteProject')))))));
};
OrderListingItem.propTypes = {
    job: T.shape(),
    isAdminOrigin: T.bool,
    isStaff: T.bool,
    style: T.object
};
export default OrderListingItem;
